/* ============================================================================
 * aps-shop.js: shared behaviour for the four new non-spa surfaces
 *   homepage, secondary collection hub, secondary collection category,
 *   secondary PDP, FAQ page.
 * ----------------------------------------------------------------------------
 * One IIFE, no dependencies, no framework. Everything is either a delegated
 * listener scoped to .aps-shop or an idempotent scan, so nothing double-binds
 * when the script is included by more than one section (Dawn loads a section's
 * assets once per section, and the theme editor re-renders sections live).
 *
 * WHAT IS IN HERE
 *   1. apsOpenChat + [data-aps-chat] delegate  (mirrors assets/spa-product.js)
 *   2. accordion / disclosure toggling
 *   3. chip filtering (multi group, OR inside a group, AND across groups)
 *   4. client-side text filter with count, clear, empty state
 *   5. chip scroll spy with smooth anchor scrolling
 *   6. gallery thumbnail switching
 *   7. sticky-offset measurement for --stick
 *   8. background video gating (wide screens, no reduced motion)
 * Every behaviour is declarative: the surface agents only add attributes.
 * See the ATTRIBUTE CONTRACT block at the bottom of this comment.
 *
 * CHAT LADDER: copied from assets/spa-product.js apsOpenChat(), including the
 * PRODUCTION retry count of 12. The static previews cut it to 2 because no
 * Inbox widget can ever mount on aps.entangle.com.au; this file runs on the
 * real store where the widget genuinely mounts, so 12 is correct here.
 *
 * ANALYTICS: identical shape to production. fbq Contact (with the aps_mc_
 * eventID and the ShopifyAnalytics product id when there is one), gtag
 * chat_click with send_to G-GDJ8QX7WM1, and a dataLayer chat_click push.
 * Every call is guarded on the global existing.
 *
 * ATTRIBUTE CONTRACT (all of it only works inside a .aps-shop wrapper)
 *   data-aps-chat="placement_handle"      chat CTA, any element
 *   data-aps-accordion                    wrapper for <details> children
 *   data-aps-accordion="single"           only one child open at a time
 *   data-aps-toggle="#id"                 button that toggles .is-open on #id
 *   data-aps-filter="KEY"                 chip bar
 *   data-aps-chip data-group data-value   chip inside that bar
 *   data-aps-filter-items="KEY"           wrapper holding the filterable items
 *   data-aps-item                         one filterable item, reads data-<group>
 *   data-aps-item-any="size type"         groups this item always passes
 *   data-aps-filter-count="KEY"           count readout, data-tpl / data-tpl-all
 *   data-aps-filter-clear="KEY"           clear button, gets [hidden] when idle
 *   data-aps-filter-empty="KEY"           empty state, gets .show when 0 match
 *   data-aps-search="KEY"                 text input
 *   data-aps-search-scope="KEY"           wrapper holding the searchable items
 *   data-aps-searchable                   one searchable item
 *   data-aps-search-group                 group wrapper, hidden when all hidden
 *   data-aps-search-count="KEY"           count readout, data-tpl / data-tpl-all
 *   data-aps-search-clear="KEY"           clear button, gets .show when typing
 *   data-aps-search-empty="KEY"           empty state, gets .show when 0 match
 *   data-aps-spy="targetId"               spy chip, gets aria-current="true",
 *                                         click smooth-scrolls to #targetId
 *   data-aps-spy-bar                      the sticky bar holding the spy chips,
 *                                         its height is part of the spy offset
 *   data-aps-gallery                      gallery root
 *   data-aps-gallery-main                 the <img> that swaps
 *   data-aps-thumb data-src               thumbnail, gets .active
 *   data-aps-video data-min-width="760"   background video gate
 * ==========================================================================*/
(function () {
  'use strict';

  var ROOT = '.aps-shop';
  var GA4 = 'G-GDJ8QX7WM1';

  /* ------------------------------------------------------------------------
   * helpers
   * ---------------------------------------------------------------------- */
  function all(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }
  function inShop(el) {
    return !!(el && el.closest && el.closest(ROOT));
  }
  function tpl(str, n, total) {
    return String(str).replace(/\{n\}/g, n).replace(/\{total\}/g, total);
  }

  /* ------------------------------------------------------------------------
   * 1. CHAT: Shopify Inbox widget
   * Ladder mirrors assets/spa-product.js exactly. Retry count is the
   * production 12, then the phone so a CTA is never a dead end.
   * ---------------------------------------------------------------------- */
  function apsOpenChat(attempt) {
    var doc = document;
    // 1. new agent widget: real public API on the <shopify-chat> element
    var sc = doc.querySelector('shopify-chat');
    if (sc && typeof sc.show === 'function') { try { sc.show(); return; } catch (err) {} }
    // 2. new agent widget: shadow activator (part="activator")
    var act = sc && sc.shadowRoot && sc.shadowRoot.querySelector('button.activator');
    if (act) { act.click(); return; }
    // 3. legacy Inbox V1 widget: shadow toggle
    var host = doc.querySelector('inbox-online-store-chat');
    var toggle = host && host.shadowRoot && host.shadowRoot.querySelector('button.chat-toggle');
    if (toggle) { toggle.click(); return; }
    // 4. still mounting? retry, then fall back to the phone so a CTA is never a dead end
    if ((attempt || 0) < 12) { setTimeout(function () { apsOpenChat((attempt || 0) + 1); }, 250); return; }
    var tel = doc.querySelector('a[href^="tel:"]');
    if (tel) { window.location.href = tel.getAttribute('href'); }
  }

  function chatClick(el) {
    // Conversion events keep the SAME ref values (placement_handle) so the
    // per-placement, per-product attribution built for the Messenger CTAs
    // survives the channel change. eventID keeps the aps_mc_ prefix for
    // continuity with any server-side CAPI dedup. GA4/GTM: the event is
    // chat_click (was messenger_click), repoint anything keyed on the old name.
    var ref = el.getAttribute('data-aps-chat') || '';
    var sid;
    try { sid = sessionStorage.getItem('aps_sid') || ('s_anon_' + Date.now()); } catch (err) { sid = 's_anon_' + Date.now(); }
    var eid = 'aps_mc_' + sid + '_' + Date.now();
    var payload = { content_name: 'Chat Click', content_category: ref };
    try {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (meta && meta.product && meta.product.id) { payload.content_ids = [String(meta.product.id)]; payload.content_type = 'product'; }
    } catch (err) {}
    if (window.fbq) { try { window.fbq('track', 'Contact', payload, { eventID: eid }); } catch (err) {} }
    if (window.gtag) { try { window.gtag('event', 'chat_click', { send_to: GA4, chat_ref: ref, page_path: location.pathname }); } catch (err) {} }
    if (window.dataLayer) { try { window.dataLayer.push({ event: 'chat_click', chat_ref: ref, chat_event_id: eid }); } catch (err) {} }
    apsOpenChat(0);
  }

  /* ------------------------------------------------------------------------
   * 3. CHIP FILTER
   * OR inside a group, AND across groups. Items declare their values as
   * data-<group> attributes; space separated values are all matched.
   * ---------------------------------------------------------------------- */
  function filterKey(el, attr) {
    return el.getAttribute(attr) || '';
  }
  function applyFilter(bar) {
    var key = filterKey(bar, 'data-aps-filter');
    var shop = bar.closest(ROOT) || document;
    var itemsWrap = shop.querySelector('[data-aps-filter-items="' + key + '"]') || shop;
    var items = all('[data-aps-item]', itemsWrap);
    var chips = all('[data-aps-chip]', bar);
    var groups = {};
    chips.forEach(function (c) {
      if (!c.classList.contains('on')) return;
      var g = c.getAttribute('data-group') || 'all';
      (groups[g] = groups[g] || []).push(c.getAttribute('data-value'));
    });
    var names = Object.keys(groups);
    var shown = 0;
    items.forEach(function (it) {
      var exempt = (it.getAttribute('data-aps-item-any') || '').split(/\s+/);
      var ok = names.every(function (g) {
        if (exempt.indexOf(g) > -1) return true;
        var have = (it.getAttribute('data-' + g) || '').split(/\s+/);
        return groups[g].some(function (v) { return have.indexOf(v) > -1; });
      });
      it.style.display = ok ? '' : 'none';
      if (ok) shown++;
    });
    var countEl = shop.querySelector('[data-aps-filter-count="' + key + '"]');
    if (countEl) {
      var total = parseInt(countEl.getAttribute('data-aps-filter-total'), 10);
      if (isNaN(total)) total = items.length;
      var one = countEl.getAttribute('data-tpl') || 'Showing {n} of {total}';
      var allT = countEl.getAttribute('data-tpl-all') || one;
      countEl.textContent = names.length ? tpl(one, shown, total) : tpl(allT, shown, total);
    }
    var clearEl = shop.querySelector('[data-aps-filter-clear="' + key + '"]');
    if (clearEl) clearEl.hidden = !names.length;
    var emptyEl = shop.querySelector('[data-aps-filter-empty="' + key + '"]');
    if (emptyEl) emptyEl.classList.toggle('show', shown === 0);
  }

  /* ------------------------------------------------------------------------
   * 4. TEXT FILTER
   * Matches the FAQ prototype behaviour: hide non-matching items, hide groups
   * that end up empty, auto-open matching <details> once the query is 2+
   * characters, restore the original open state when the query is cleared.
   * ---------------------------------------------------------------------- */
  function searchScope(input) {
    var key = filterKey(input, 'data-aps-search');
    var shop = input.closest(ROOT) || document;
    return { key: key, shop: shop, scope: shop.querySelector('[data-aps-search-scope="' + key + '"]') || shop };
  }
  function applySearch(input) {
    var ctx = searchScope(input);
    var items = all('[data-aps-searchable]', ctx.scope);
    var q = (input.value || '').trim().toLowerCase();
    var shown = 0;
    items.forEach(function (it) {
      if (!it.hasAttribute('data-aps-text')) {
        it.setAttribute('data-aps-text', (it.textContent || '').toLowerCase());
        if (it.tagName === 'DETAILS') it.setAttribute('data-aps-open-default', it.hasAttribute('open') ? '1' : '0');
      }
      var hit = !q || it.getAttribute('data-aps-text').indexOf(q) !== -1;
      it.style.display = hit ? '' : 'none';
      if (hit) shown++;
      if (it.tagName === 'DETAILS') {
        if (q.length >= 2) { it.open = hit; }
        else if (!q) { it.open = it.getAttribute('data-aps-open-default') === '1'; }
      }
    });
    all('[data-aps-search-group]', ctx.scope).forEach(function (g) {
      var any = all('[data-aps-searchable]', g).some(function (it) { return it.style.display !== 'none'; });
      g.style.display = any ? '' : 'none';
    });
    var countEl = ctx.shop.querySelector('[data-aps-search-count="' + ctx.key + '"]');
    if (countEl) {
      var total = items.length;
      var one = countEl.getAttribute('data-tpl') || '{n} of {total} match';
      var allT = countEl.getAttribute('data-tpl-all') || '{total} items';
      countEl.textContent = q ? tpl(one, shown, total) : tpl(allT, shown, total);
    }
    var clearEl = ctx.shop.querySelector('[data-aps-search-clear="' + ctx.key + '"]');
    if (clearEl) clearEl.classList.toggle('show', !!q);
    var emptyEl = ctx.shop.querySelector('[data-aps-search-empty="' + ctx.key + '"]');
    if (emptyEl) emptyEl.classList.toggle('show', shown === 0);
  }

  /* ------------------------------------------------------------------------
   * 5. SPY: which group is under the sticky bars
   * ---------------------------------------------------------------------- */
  function stickyOffset(shop) {
    var h = 0;
    var hdr = document.querySelector('sticky-header');
    if (hdr && hdr.offsetHeight) h += hdr.offsetHeight;
    var bar = shop && shop.querySelector('[data-aps-spy-bar]');
    if (bar && bar.offsetHeight) h += bar.offsetHeight;
    return h;
  }
  function spy() {
    all(ROOT).forEach(function (shop) {
      var chips = all('[data-aps-spy]', shop);
      if (!chips.length) return;
      var offset = stickyOffset(shop) + 30;
      var current = null;
      var ids = chips.map(function (c) { return c.getAttribute('data-aps-spy'); });
      var targets = ids.map(function (id) { return document.getElementById(id); });
      targets.forEach(function (t) {
        if (!t || t.style.display === 'none') return;
        if (t.getBoundingClientRect().top <= offset) current = t.id;
      });
      if (!current) {
        var first = targets.filter(function (t) { return t && t.style.display !== 'none'; })[0];
        current = first ? first.id : null;
      }
      chips.forEach(function (c) {
        if (c.getAttribute('data-aps-spy') === current) c.setAttribute('aria-current', 'true');
        else c.removeAttribute('aria-current');
      });
    });
  }

  /* ------------------------------------------------------------------------
   * 7. --stick: how far down the sticky page bars have to sit.
   * Dawn's header is only sticky when enable_sticky_header is on, in which
   * case it renders as <sticky-header>. Otherwise the offset is 0.
   * ---------------------------------------------------------------------- */
  function setStick() {
    var hdr = document.querySelector('sticky-header');
    var h = hdr && hdr.offsetHeight ? hdr.offsetHeight : 0;
    all(ROOT).forEach(function (shop) { shop.style.setProperty('--stick', h + 'px'); });
  }

  /* ------------------------------------------------------------------------
   * 8. background video gating
   * Play only on wide screens without reduced motion, otherwise the poster
   * image underneath is the experience.
   * ---------------------------------------------------------------------- */
  var still = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Belt and braces for the <source media> split. Chrome and Safari honour
   * media on <source>, so phones pick the small encode and desktops the wide
   * one. Any engine that ignores media takes the FIRST source, which is the
   * mobile file, so a phone can never pull the wide file. This upgrades such a
   * desktop back to the wide file once, guarded so it cannot loop. */
  function upgradeSource(v) {
    if (!v || v.getAttribute('data-aps-upgraded') === '1') return;
    var wide = v.getAttribute('data-src-wide');
    if (!wide) return;
    if (!window.matchMedia('(min-width:760px)').matches) return;
    var cur = v.currentSrc || '';
    if (!cur || cur.indexOf(wide) !== -1) return;
    v.setAttribute('data-aps-upgraded', '1');
    var src = v.querySelector('source:last-of-type');
    if (src) { src.setAttribute('src', wide); }
    v.src = wide;
    if (v.load) v.load();
  }

  function gateVideo(v) {
    if (!v) return;
    // No data-min-width means no width gate at all (the hero: it autoplays on
    // phones off the mobile encode). The showroom band still sets 760.
    var min = parseInt(v.getAttribute('data-min-width'), 10);
    if (isNaN(min)) min = 0;
    var wide = min <= 0 || window.matchMedia('(min-width:' + min + 'px)').matches;
    if (wide && !still.matches) {
      upgradeSource(v);
      // muted must be true for iOS to allow autoplay. The attribute is in the
      // markup; this only reasserts it if something stripped it.
      v.muted = true;
      if (v.play) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
    }
    else if (v.pause) { v.pause(); }
  }
  function gateAllVideos() {
    all(ROOT + ' [data-aps-video]').forEach(gateVideo);
  }

  /* ------------------------------------------------------------------------
   * idempotent scan: per element setup that cannot be delegated
   * ---------------------------------------------------------------------- */
  function scan() {
    setStick();

    all(ROOT + ' [data-aps-filter]').forEach(function (bar) { applyFilter(bar); });
    all(ROOT + ' [data-aps-search]').forEach(function (input) { applySearch(input); });

    all(ROOT + ' [data-aps-video]').forEach(function (v) {
      if (v.getAttribute('data-aps-bound') === '1') return;
      v.setAttribute('data-aps-bound', '1');
      v.addEventListener('loadeddata', function () { gateVideo(v); });
      // autoplay can be rejected (low power mode, browser policy): start it
      // lazily when it nears the viewport, and retry on the first interaction.
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (es) {
          es.forEach(function (e) {
            if (!e.isIntersecting) return;
            if (v.getAttribute('preload') === 'none') v.preload = 'auto';
            gateVideo(v);
            io.disconnect();
          });
        }, { rootMargin: '300px' });
        io.observe(v);
      } else {
        gateVideo(v);
      }
    });

    spy();
  }

  /* ------------------------------------------------------------------------
   * init: delegated listeners bind once, scans are safe to repeat
   * ---------------------------------------------------------------------- */
  function init() {
    if (!window.__apsShopInit) {
      window.__apsShopInit = true;
      var doc = document;

      // ---- click delegate, everything scoped to .aps-shop ----
      doc.addEventListener('click', function (e) {
        var t = e.target;
        if (!t || !t.closest) return;

        // chat CTA
        var chat = t.closest(ROOT + ' [data-aps-chat]');
        if (chat) { e.preventDefault(); chatClick(chat); return; }

        // filter chip
        var chip = t.closest(ROOT + ' [data-aps-chip]');
        if (chip) {
          var bar = chip.closest('[data-aps-filter]');
          if (bar) {
            e.preventDefault();
            chip.classList.toggle('on');
            chip.setAttribute('aria-pressed', chip.classList.contains('on') ? 'true' : 'false');
            applyFilter(bar);
            return;
          }
        }

        // filter clear
        var fclear = t.closest(ROOT + ' [data-aps-filter-clear]');
        if (fclear) {
          e.preventDefault();
          var fkey = filterKey(fclear, 'data-aps-filter-clear');
          var fbar = (fclear.closest(ROOT) || doc).querySelector('[data-aps-filter="' + fkey + '"]');
          if (fbar) {
            all('[data-aps-chip]', fbar).forEach(function (c) { c.classList.remove('on'); c.setAttribute('aria-pressed', 'false'); });
            applyFilter(fbar);
          }
          return;
        }

        // search clear
        var sclear = t.closest(ROOT + ' [data-aps-search-clear]');
        if (sclear) {
          e.preventDefault();
          var skey = filterKey(sclear, 'data-aps-search-clear');
          var input = (sclear.closest(ROOT) || doc).querySelector('[data-aps-search="' + skey + '"]');
          if (input) { input.value = ''; applySearch(input); input.focus(); }
          return;
        }

        // spy chip: smooth scroll (the global html{scroll-behavior:smooth} was
        // stripped from the port, so do it here rather than restyle the theme)
        var spyChip = t.closest(ROOT + ' [data-aps-spy]');
        if (spyChip) {
          var target = doc.getElementById(spyChip.getAttribute('data-aps-spy'));
          if (target) {
            e.preventDefault();
            target.scrollIntoView({ behavior: still.matches ? 'auto' : 'smooth', block: 'start' });
          }
          return;
        }

        // gallery thumbnail
        var thumb = t.closest(ROOT + ' [data-aps-thumb]');
        if (thumb) {
          e.preventDefault();
          var gal = thumb.closest('[data-aps-gallery]');
          var main = gal && gal.querySelector('[data-aps-gallery-main]');
          if (gal) all('[data-aps-thumb]', gal).forEach(function (x) { x.classList.remove('active'); });
          thumb.classList.add('active');
          if (main) {
            if (thumb.getAttribute('data-src')) main.src = thumb.getAttribute('data-src');
            if (thumb.getAttribute('data-srcset')) main.srcset = thumb.getAttribute('data-srcset');
            if (thumb.getAttribute('data-alt') !== null) main.alt = thumb.getAttribute('data-alt');
          }
          return;
        }

        // generic disclosure toggle
        var toggle = t.closest(ROOT + ' [data-aps-toggle]');
        if (toggle) {
          var sel = toggle.getAttribute('data-aps-toggle');
          var panel = sel ? doc.querySelector(sel) : null;
          if (panel) {
            e.preventDefault();
            var open = panel.classList.toggle('is-open');
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          }
          return;
        }
      });

      // ---- accordion: single open group (details fire toggle, which does not
      // bubble, so listen in the capture phase) ----
      doc.addEventListener('toggle', function (e) {
        var d = e.target;
        if (!d || d.tagName !== 'DETAILS' || !d.open || !inShop(d)) return;
        var group = d.closest('[data-aps-accordion]');
        if (!group || group.getAttribute('data-aps-accordion') !== 'single') return;
        all('details', group).forEach(function (other) {
          if (other !== d && other.closest('[data-aps-accordion]') === group) other.open = false;
        });
      }, true);

      // ---- text filter: input bubbles, so one delegate covers every surface ----
      doc.addEventListener('input', function (e) {
        var input = e.target;
        if (!input || !input.hasAttribute || !input.hasAttribute('data-aps-search') || !inShop(input)) return;
        applySearch(input);
      });
      doc.addEventListener('search', function (e) {
        var input = e.target;
        if (!input || !input.hasAttribute || !input.hasAttribute('data-aps-search') || !inShop(input)) return;
        applySearch(input);
      });

      // ---- passive listeners ----
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { spy(); ticking = false; });
      }, { passive: true });
      window.addEventListener('resize', function () { setStick(); gateAllVideos(); });
      window.addEventListener('load', function () { setStick(); gateAllVideos(); });
      doc.addEventListener('visibilitychange', function () { if (!doc.hidden) gateAllVideos(); });
      if (still.addEventListener) still.addEventListener('change', gateAllVideos);
      var kick = function () {
        gateAllVideos();
        ['pointerdown', 'keydown', 'touchstart'].forEach(function (n) { doc.removeEventListener(n, kick); });
      };
      ['pointerdown', 'keydown', 'touchstart'].forEach(function (n) { doc.addEventListener(n, kick, { passive: true }); });

      // theme editor: sections re-render, so rescan (delegates stay bound)
      doc.addEventListener('shopify:section:load', scan);
    }

    scan();
  }

  // public handle, mostly so a section can open chat from its own inline code
  window.apsShop = window.apsShop || { openChat: apsOpenChat, refresh: scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
